import { useState, useMemo } from "react";
import AppHeader from "@/components/AppHeader";
import { cations, anions, getSolubility, type Ion, type Solubility } from "@/data/ions";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

export default function IonEngine() {
  const [search, setSearch] = useState("");
  const [selectedCation, setSelectedCation] = useState<Ion | null>(null);
  const [selectedAnion, setSelectedAnion] = useState<Ion | null>(null);
  const [tab, setTab] = useState<"cations" | "anions">("cations");

  const allIons = tab === "cations" ? cations : anions;

  const filtered = useMemo(() => {
    if (!search) return allIons;
    const q = search.toLowerCase();
    return allIons.filter(
      (ion) => ion.name.toLowerCase().includes(q) || ion.formula.toLowerCase().includes(q)
    );
  }, [search, allIons]);

  const combination = useMemo(() => {
    if (!selectedCation || !selectedAnion) return null;
    return getSolubility(selectedCation, selectedAnion);
  }, [selectedCation, selectedAnion]);

  const solLabel: Record<Solubility, { text: string; color: string }> = {
    S: { text: "Soluble", color: "text-primary" },
    I: { text: "Insoluble", color: "text-destructive" },
    sS: { text: "Slightly Soluble", color: "text-foreground" },
  };

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Ion Charges & Formulas — ElementPad" description="Practice ionic compound formulas with common cations and anions. Learn polyatomic ions and oxidation states." path="/ions" />
      <AppHeader />
      <Main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Ion Engine</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Browse common ions and check solubility when combining a cation with an anion.
        </p>

        {/* Combination panel */}
        <div className="rounded-lg border border-border bg-tile-bg p-4 mb-6">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Ion Combination</p>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => { setTab("cations"); setSelectedCation(null); }}
              className={`px-4 py-2 rounded-lg border font-mono text-sm transition-colors ${
                selectedCation
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary"
              }`}
            >
              {selectedCation ? selectedCation.formula : "Select cation (+)"}
            </button>
            <span className="text-muted-foreground">+</span>
            <button
              onClick={() => { setTab("anions"); setSelectedAnion(null); }}
              className={`px-4 py-2 rounded-lg border font-mono text-sm transition-colors ${
                selectedAnion
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary"
              }`}
            >
              {selectedAnion ? selectedAnion.formula : "Select anion (−)"}
            </button>
            {combination && (
              <>
                <span className="text-muted-foreground">=</span>
                <span className={`font-semibold ${solLabel[combination].color}`}>
                  {solLabel[combination].text}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 bg-muted rounded-lg p-1 mb-4 w-fit">
          {(["cations", "anions"] as const).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setSearch(""); }}
              className={`text-sm font-medium py-1.5 px-4 rounded-md transition-colors capitalize ${
                tab === t ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Search */}
        <label htmlFor="ion-search" className="sr-only">Search {tab}</label>
        <input
          id="ion-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${tab}...`}
          className="w-full h-10 rounded-lg border border-input bg-tile-bg px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring mb-4"
        />

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {filtered.map((ion) => {
            const isSelected =
              (tab === "cations" && selectedCation?.formula === ion.formula && selectedCation?.name === ion.name) ||
              (tab === "anions" && selectedAnion?.formula === ion.formula && selectedAnion?.name === ion.name);
            return (
              <button
                key={ion.formula + ion.name}
                onClick={() => {
                  if (tab === "cations") setSelectedCation(ion);
                  else setSelectedAnion(ion);
                }}
                className={`flex flex-col items-start p-3 rounded-lg border transition-all text-left ${
                  isSelected
                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                    : "border-border bg-tile-bg hover:bg-tile-hover hover:border-primary/30"
                }`}
              >
                <div className="flex items-center gap-2 w-full">
                  <span className="font-mono text-base font-semibold text-foreground">{ion.formula}</span>
                  {ion.flameColor && (
                    <span
                      className="w-3 h-3 rounded-full border border-border shrink-0"
                      style={{ backgroundColor: ion.flameColor }}
                      title={`Flame test: ${ion.flameColorName}`}
                    />
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{ion.name}</span>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] font-mono text-muted-foreground">
                  <span>Charge: {ion.charge > 0 ? `+${ion.charge}` : ion.charge}</span>
                  {ion.molarMass && <span>{ion.molarMass.toFixed(2)} g/mol</span>}
                </div>
                {ion.flameColorName && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Flame: {ion.flameColorName}
                  </p>
                )}
                {ion.commonCompounds && ion.commonCompounds.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {ion.commonCompounds.slice(0, 3).map((c) => (
                      <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Main>
    </div>
  );
}
